/**
 * §11.7 系統維護畫面。**讀者是非工程人員。**
 *
 * 規格書的原話：「這個畫面的每一則異常都要寫清楚**下一步該做什麼、該找誰**，
 * 不能只顯示紅燈。使用者是非工程人員，『投影落後 1,247』對他沒有意義，
 * 『資料同步落後，請聯絡 IT 並提供此畫面截圖』才有。」
 *
 * 所以這一頁刻意**不顯示** projectionLag 那個數字當主要資訊——
 * 它只在「詳細資訊」裡出現，是給 IT 看的。主畫面顯示的是綠燈／紅燈與該做什麼。
 */

import { useQuery } from '../../api/hooks.js'
import { Alert, Button, Card, KV, Mono, Pill } from '../../components/ui.js'
import { count, ts } from '../../../../shared/lib/format.js'

interface SystemStatus {
  service: {
    database: 'ok' | 'down'
    projectionLag: number | null
    projectionOk: boolean
    apiUptimeSeconds: number
    checkedAt: string
  }
  backup: { lastDumpAt: string | null; lastDumpBytes: number | null; lastRestoreVerifiedAt: string | null }
  events: { total: number; today: number; lastAt: string | null }
  disk: { databaseBytes: number; databasePretty: string | null }
  anomalies: { severity: 'warning' | 'critical'; title: string; whatToDo: string }[]
}

export function AdminSystem() {
  const { data, error, loading, reload } = useQuery<SystemStatus>('/api/admin/system')

  if (loading) return <p className="text-sm text-ink-500">載入中…</p>
  if (error) return <Alert tone="danger" title="讀不到系統狀態">{error.message}</Alert>
  if (!data) return null

  const critical = data.anomalies.filter((a) => a.severity === 'critical')
  const warnings = data.anomalies.filter((a) => a.severity === 'warning')

  return (
    <div className="space-y-4">
      <Card
        title="系統維護"
        subtitle={`最後檢查 ${ts(data.service.checkedAt)}`}
        right={<Button size="sm" onClick={reload}>重新檢查</Button>}
        tone={critical.length > 0 ? 'danger' : warnings.length > 0 ? 'warn' : 'ok'}
      >
        {data.anomalies.length === 0 ? (
          <p className="text-sm text-emerald-800">
            ✓ 目前沒有需要處理的事情。系統正常運作中。
          </p>
        ) : (
          <div className="space-y-3">
            {[...critical, ...warnings].map((a, i) => (
              <div
                key={i}
                className={`rounded-md border p-3 ${a.severity === 'critical'
                  ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
              >
                <p className={`text-sm font-semibold ${a.severity === 'critical' ? 'text-red-900' : 'text-amber-900'}`}>
                  {a.severity === 'critical' ? '需要處理' : '請留意'}：{a.title}
                </p>
                {/* 這一行是整個畫面的重點：該做什麼、該找誰 */}
                <p className={`mt-1 text-sm ${a.severity === 'critical' ? 'text-red-800' : 'text-amber-800'}`}>
                  → {a.whatToDo}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="服務狀態">
        <div className="grid gap-3 sm:grid-cols-2">
          <Light
            label="資料庫連線"
            ok={data.service.database === 'ok'}
            okText="正常"
            badText="連不上——請立即截圖後聯絡 IT，不要自己重啟"
          />
          <Light
            label="資料同步"
            ok={data.service.projectionOk}
            okText="正常"
            badText="落後——畫面上的數字可能不是最新的，但作業紀錄沒有遺失。請截圖後聯絡 IT"
          />
        </div>
      </Card>

      <Card title="備份">
        <KV items={[
          ['最後一次備份', data.backup.lastDumpAt
            ? ts(data.backup.lastDumpAt)
            : <span key="a" className="text-red-700">從來沒有備份成功過</span>],
          ['備份大小', data.backup.lastDumpBytes
            ? `${(data.backup.lastDumpBytes / 1024 / 1024).toFixed(1)} MB` : '—'],
          ['最後一次還原驗證', data.backup.lastRestoreVerifiedAt
            ? ts(data.backup.lastRestoreVerifiedAt)
            : <span key="b" className="text-amber-700">從來沒有做過</span>],
        ]} />
        <p className="mt-3 text-xs text-ink-500">
          沒驗證過的備份等於沒有備份。還原演練每月要做一次——那是確認「真的還原得回來」的唯一方法。
        </p>
      </Card>

      <Card title="資料量">
        <KV items={[
          ['事件總數', count(data.events.total)],
          ['今日新增', count(data.events.today)],
          ['最後一筆', data.events.lastAt ? ts(data.events.lastAt) : '—'],
          ['資料庫容量', data.disk.databasePretty ?? '—'],
        ]} />
      </Card>

      <details className="rounded-lg border border-ink-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-medium text-ink-700">
          詳細資訊（給 IT 看的）
        </summary>
        <div className="mt-3">
          <KV items={[
            ['投影落後量', <Mono key="a">{String(data.service.projectionLag ?? '（無法取得）')}</Mono>],
            ['API 已執行', `${Math.floor(data.service.apiUptimeSeconds / 3600)} 小時 ${Math.floor((data.service.apiUptimeSeconds % 3600) / 60)} 分`],
            ['資料庫位元組', <Mono key="b">{count(data.disk.databaseBytes)}</Mono>],
          ]} />
          <p className="mt-3 text-xs text-ink-500">
            投影落後不為 0 時，容器內的
            <Mono className="mx-1">node src/cli.js projection:verify</Mono>
            會逐表比對出差異；必要時用
            <Mono className="mx-1">projection:rebuild</Mono>
            從事件重建（需停機）。維運手冊有逐步說明。
          </p>
        </div>
      </details>
    </div>
  )
}

function Light({ label, ok, okText, badText }: {
  label: string; ok: boolean; okText: string; badText: string
}) {
  return (
    <div className={`rounded-md border p-3 ${ok ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-block h-3 w-3 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
        <span className="text-sm font-medium text-ink-900">{label}</span>
        <Pill tone={ok ? 'emerald' : 'red'}>{ok ? '正常' : '異常'}</Pill>
      </div>
      <p className={`mt-1.5 text-xs ${ok ? 'text-emerald-800' : 'text-red-800'}`}>
        {ok ? okText : badText}
      </p>
    </div>
  )
}
